import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { cn } from '@/lib/cn';
import { vendorLabel } from '@/lib/providers/integration-state';
import type { AccountCredentials, VendorAccount } from '@/lib/providers/providers-overview';

/**
 * EACH VENDOR ACCOUNT ONCE (D1). A vendor that serves several services here
 * (Sabre: hotels + air tickets) is ONE account — one credential set, one
 * contract (D15) — that provides N services. This strip says so once, in a
 * line per account: name, whether the credential is there, and what it
 * provides. The rows in each service card are that account's BINDINGS and point
 * back here instead of repeating it. A vendor that serves one service is just
 * its row; nothing here.
 */

const CREDENTIALS_TONE: Record<AccountCredentials, string> = {
  present: 'text-emerald-700 dark:text-emerald-400',
  partial: 'text-amber-700 dark:text-amber-400',
  missing: 'font-semibold text-amber-700 dark:text-amber-400',
  absent: 'text-muted-foreground',
};

export function VendorAccounts({ accounts }: { accounts: readonly VendorAccount[] }) {
  const { t } = useTranslation('network');
  const headingId = useId();
  if (accounts.length === 0) return null;
  const service = (s: string) => t(`providers.integrations.category.${s}`, { defaultValue: s });

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border bg-card px-4 py-2.5 text-sm"
      data-testid="provider-accounts"
    >
      <h2 id={headingId} className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className="font-semibold uppercase tracking-wider text-muted-foreground">{t('providers.account.title')}</span>
        <span className="text-muted-foreground">{t('providers.account.hint')}</span>
      </h2>
      <ul className="mt-1 divide-y">
        {accounts.map((account) => (
          <li
            key={account.vendor}
            className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5"
            data-testid={`provider-account-${account.vendor}`}
            data-credentials={account.credentials}
            data-services={account.services.join(' ')}
          >
            <span className="flex min-w-0 items-center gap-1.5 font-medium" title={account.vendor}>
              <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{vendorLabel(t, account.services[0] ?? '', account.vendor)}</span>
            </span>
            <span
              className={cn('text-xs', CREDENTIALS_TONE[account.credentials])}
              data-testid={`provider-account-credentials-${account.vendor}`}
            >
              {t(`providers.account.credentials.${account.credentials}`, {
                services: account.lacking.map(service).join(' · '),
              })}
            </span>
            <span className="text-xs text-muted-foreground" data-testid={`provider-account-services-${account.vendor}`}>
              {t('providers.account.provides', { services: account.services.map(service).join(' · ') })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
